
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { findSupportedLocale } from '@/utils/i18n-helpers';
import { scrollIntoViewIfNeeded } from '@/utils/helpers';
import { validateStudentFields, getSchoolDeliveryOptions } from '@/utils/helpers';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import MultiSelect from '@/components/shared/MultiSelect.vue';

const namespace = 'auth';

@Component({
	components: { ToggleSwitch, ValidationProvider, MultiSelect }
})
export default class DeliverySchool extends Vue {
	@Prop({ type: Object, required: true, default: () => {} }) private restaurantDeliveryInfo!: SchoolDelivery;
	@Prop({ type: Object, default: () => ({}) }) private deliveryInformation!: CheckoutDeliveryInfo;
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('getUserStudents', { namespace }) private students!: UserStudent[];
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;

	$refs!: { 'student-select': any };

	private delivery: CheckoutDeliveryInfo = { type: 'school' };
	private schools: (string | undefined)[] | null = [];
	private saveStudentInformation: boolean = false;

	/**
	 * This getter fetches the school delivery information.
	 * It is used to populate the school's dependant fields
	 *
	 * @return {SchoolDeliveryOptions}
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions {
		return getSchoolDeliveryOptions(this.restaurantDeliveryInfo, this.delivery.school);
	}

	/**
	 * Check if a student belongs to the current restaurant
	 * Students without a restaurantId are treated as belonging to the current location (backward compatibility)
	 *
	 * @param {UserStudent} student
	 * @return {boolean}
	 */
	private isStudentAtCurrentLocation(student: UserStudent): boolean {
		return !student.restaurantId || student.restaurantId === this.restaurant.app8_restaurant;
	}

	/**
	 * Get student names for dropdown options, filtered by current location
	 * Only includes students from the current restaurant or students without a restaurantId
	 *
	 * @return {string[]}
	 */
	private get studentOptions(): string[] {
		return this.students
			?.filter(student => this.isStudentAtCurrentLocation(student))
			.map(student => student.name || '')
			.filter(Boolean) || [];
	}

	/**
	 * Get filtered extra options (non-empty items, trimmed)
	 *
	 * @return {string[]}
	 */
	private get filteredExtra(): string[] {
		return this.schoolDeliveryOptions.extra?.filter(item => item?.trim()).map(item => item.trim()) || [];
	}

	/**
	 * Convert extra string from extra field to array for the MultiSelect component
	 *
	 * @return {string[]}
	 */
	private get extraArray(): string[] {
		const info = this.delivery.extra;
		if (!info || typeof info !== 'string') {
			return [];
		}
		return info.split(',').map(item => item.trim()).filter(item => item.length);
	}

	/**
	 * Set schools in the selectors
	 *
	 * @return {void}
	 */
	private created(): void {
		this.schools = this.restaurantDeliveryInfo.schools?.map((school) => school.name);
	}

	/**
	 * Fetch this user's students
	 * Reset the delivery info
	 * Update school to default school if only one school is available
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if (this.deliveryInformation) {
			this.delivery = { ...this.deliveryInformation, type: 'school' };
		}
		if (this.restaurantDeliveryInfo.schools.length === 1 && this.delivery.school !== this.restaurantDeliveryInfo.schools[0].name) {
			this.delivery.school = this.restaurantDeliveryInfo.schools[0].name;
		}
		// Build the address if delivery info exists
		if (this.delivery.student) {
			this.$nextTick(() => {
				this.updateDeliveryInfo();
			});
		}
	}

	/**
	 * Handle save student toggle change (free-text input flow)
	 *
	 * @param {boolean} newValue
	 * @return {void}
	 */
	private onSaveToggleChange(newValue: boolean): void {
		this.$emit('create-delivery-info', newValue);
		this.saveStudentInformation = newValue;
	}

	/**
	 * Populate the student's values in the input fields.
	 * * Only for logged in users.
	 *
	 * @return {void}
	 */
	private onStudentChange(): void {
		if (this.user?.id){
			const studentName = this.delivery.student;
			const existing = this.students.find(student =>
				student.name === studentName && this.isStudentAtCurrentLocation(student)
			);
			const defaultSchoolName = this.restaurantDeliveryInfo.schools.length === 1 ? this.restaurantDeliveryInfo.schools[0].name : '';
			if (existing) {
				this.delivery = {
					...existing,
					type: 'school',
					name: studentName,
					student: studentName,
					school: existing.school || defaultSchoolName,
					extra: existing.extra || ''
				};
				validateStudentFields({ student: this.delivery, restaurantDeliveryInfo: this.restaurantDeliveryInfo, schoolDeliveryOptions: this.schoolDeliveryOptions });
			}
		}
		this.updateDeliveryInfo();
		this.$nextTick(() => this.$refs['student-select']?.searchEl?.blur());
	}

	/**
	 * When we change the school, we empty the school-related inputs.
	 * We then populate that school's related values: grades, classrooms, teachers
	 * Teachers could be a select or a free form text, depending on the school.
	 *
	 * @return {void}
	 */
	private onSchoolChange(): void {
		this.delivery.grade = '';
		this.delivery.classroom = '';
		this.delivery.teacher = '';
		this.delivery.extra = '';
		this.updateDeliveryInfo();
	}

	/**
	 * Update extra from array (multi-select)
	 * Converts array to comma-separated string and stores in extra field
	 *
	 * @param {string[]} value
	 * @return {void}
	 */
	private updateExtraArray(value: string[]): void {
		this.$set(this.delivery, 'extra', value.join(','));
		this.updateDeliveryInfo();
	}

	/**
	 * Update extra from text input
	 * Trims the value and stores in extra field
	 *
	 * @param {string} value
	 * @return {void}
	 */
	private updateExtraText(value: string): void {
		this.delivery.extra = value.trim();
		this.updateDeliveryInfo();
	}

	/**
	 * Select a student by name
	 * * Called by parent after modal creates a student
	 *
	 * @param {string} studentName
	 * @return {void}
	 */
	public selectStudent(studentName: string): void {
		this.delivery.student = studentName;
		this.onStudentChange();
	}

	/**
	 * Send event to the parent to update the delivery
	 * information in the vuex store
	 *
	 * @return {void}
	 */
	private updateDeliveryInfo(): void {
		// If the restaurant has locales, set address with localization
		if (this.restaurant.locales?.length && this.restaurant.locales.length > 1) {
			this.restaurant.locales.forEach((locale: RestaurantLocale) => {
				const supportedLocale: Locale | undefined = findSupportedLocale(locale.locale_short);
				// Set the default locale as the address
				if (supportedLocale && locale.is_default) {
					this.delivery.address = this.buildAddress(supportedLocale.code);
				}
				// Set the address for the other supported locales
				else if (supportedLocale) {
					this.delivery.localization = {
						...this.delivery.localization,
						[locale.locale_short]: {
							address: this.buildAddress(supportedLocale.code)
						}
					};
				}
			});
		}
		// If no locale, just set the address
		else {
			this.delivery.address = this.buildAddress();
		}

		this.$emit('input', this.delivery);
	}

	/**
	 * Build the address string for the delivery object, in the correct locale if applicable.
	 * Add each school delivery field together, depending on which fields are in the delivery object.
	 *
	 * @param {string} [localeCode]
	 * @return {string} Output example: 'School: Mushroom Kingdom, Grade: 1, Classroom: A, Teacher: Mario, Student: Luigi, Allergens: Peanuts'
	 */
	private buildAddress(localeCode?: string): string {
		const schoolFields: (keyof CheckoutDeliveryInfo)[] = ['school', 'grade', 'classroom', 'teacher', 'student', 'extra'];
		const address: string[] = [];

		schoolFields.forEach((field) => {
			const value = this.delivery[field];
			if (value) {
				const localizedAddress = localeCode
					? this.$t(`checkout.form.delivery.school.${field}_address`, localeCode, { [field]: value })
					: this.$t(`checkout.form.delivery.school.${field}_address`, { [field]: value });
				address.push(localizedAddress);
			}
		});

		return address.join(', ');
	}

	/**
	 * Scroll to the bottom when opening a selector
	 *
	 * @param {string} elementId
	 * @return {void}
	 */
	private opened(elementId: string): void {
		setTimeout(() => {
			let contentDiv = document.querySelector(`#${elementId} .vs__dropdown-menu`);
			contentDiv && scrollIntoViewIfNeeded(contentDiv, { behavior: 'smooth', block: 'end', inline: 'start' });
		}, 100);
	}
}
